import { useState, useEffect } from 'react';
import { DollarSign, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format, parseISO, isWithinInterval, addMonths, isSameMonth } from 'date-fns';

function FinancialSummaryPage() {
  const [loading, setLoading] = useState(true);
  const [yearlyTotal, setYearlyTotal] = useState(0);
  const [financialSummary, setFinancialSummary] = useState({
    invoiced: 0,
    reserve: 0,
    settlementAgreed: 0,
    paid: 0
  });
  
  // Store financial year totals
  const [financialYearTotals, setFinancialYearTotals] = useState({
    invoiced: 0,
    reserve: 0,
    settlementAgreed: 0,
    paid: 0
  });
  
  // Toggle between current month and financial year view
  const [viewMode, setViewMode] = useState<'currentMonth' | 'financialYear'>('currentMonth');
  
  // Store historical monthly data
  const [historicalMonthlyData, setHistoricalMonthlyData] = useState<Array<{
    month: string;
    year: number;
    invoiced: number;
    reserve: number;
    settlementAgreed: number;
    paid: number;
  }>>([]);

  useEffect(() => {
    // Set up real-time subscription for payments and cases
    const paymentsSubscription = supabase
      .channel('payments-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'cases'
        }, 
        () => {
          fetchFinancialSummary();
          fetchMonthlyTotals();
          fetchHistoricalMonthlyData();
          fetchFinancialYearTotals();
        }
      )
      .subscribe();

    fetchFinancialSummary();
    fetchMonthlyTotals();
    fetchHistoricalMonthlyData();
    fetchFinancialYearTotals();

    return () => {
      supabase.removeChannel(paymentsSubscription);
    };
  }, []);

  const getCurrentFinancialYear = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // If we're before July, FY started last year
    const fyStartYear = currentMonth < 6 ? currentYear - 1 : currentYear;
    const fyStart = new Date(fyStartYear, 6, 1); // July 1st
    const fyEnd = new Date(fyStartYear + 1, 5, 30); // June 30th next year
    
    return { fyStart, fyEnd };
  };

  // Fetch current month financial summary
  const fetchFinancialSummary = async () => {
    try {
      const { fyStart, fyEnd } = getCurrentFinancialYear();
      const currentMonth = new Date();

      const { data: cases, error } = await supabase
        .from('cases')
        .select('invoiced_amount, reserve_amount, settlement_amount, paid_amount, payment_date, created_at')
        .gte('created_at', fyStart.toISOString())
        .lte('created_at', fyEnd.toISOString());

      if (error) throw error;

      // Filter for current month only
      const currentMonthCases = cases.filter(case_ => {
        const caseDate = parseISO(case_.created_at);
        return isSameMonth(caseDate, currentMonth);
      });

      const summary = currentMonthCases.reduce((acc, case_) => ({
        invoiced: acc.invoiced + (Number(case_.invoiced_amount) || 0),
        reserve: acc.reserve + (Number(case_.reserve_amount) || 0),
        settlementAgreed: acc.settlementAgreed + (Number(case_.settlement_amount) || 0),
        paid: acc.paid + (Number(case_.paid_amount) || 0)
      }), {
        invoiced: 0,
        reserve: 0,
        settlementAgreed: 0,
        paid: 0
      });

      setFinancialSummary(summary);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };
  
  // Fetch financial year totals
  const fetchFinancialYearTotals = async () => {
    try {
      const { fyStart, fyEnd } = getCurrentFinancialYear();

      const { data: cases, error } = await supabase
        .from('cases')
        .select('invoiced_amount, reserve_amount, settlement_amount, paid_amount, created_at')
        .gte('created_at', fyStart.toISOString())
        .lte('created_at', fyEnd.toISOString());

      if (error) throw error;

      const totals = cases.reduce((acc, case_) => ({
        invoiced: acc.invoiced + (Number(case_.invoiced_amount) || 0),
        reserve: acc.reserve + (Number(case_.reserve_amount) || 0),
        settlementAgreed: acc.settlementAgreed + (Number(case_.settlement_amount) || 0),
        paid: acc.paid + (Number(case_.paid_amount) || 0)
      }), {
        invoiced: 0,
        reserve: 0,
        settlementAgreed: 0,
        paid: 0
      });

      setFinancialYearTotals(totals);
    } catch (error) {
      console.error('Error fetching financial year totals:', error);
    }
  };

  // Fetch historical monthly data (end-of-month figures for each month in the financial year)
  const fetchHistoricalMonthlyData = async () => {
    try {
      const { fyStart, fyEnd } = getCurrentFinancialYear();
      const currentMonth = new Date();
      
      const { data: cases, error } = await supabase
        .from('cases')
        .select('invoiced_amount, reserve_amount, settlement_amount, paid_amount, created_at')
        .gte('created_at', fyStart.toISOString())
        .lte('created_at', fyEnd.toISOString());

      if (error) throw error;

      // Initialize all months in the FY
      const monthlyData = [];
      let monthDate = new Date(fyStart);
      
      // Only include months that have already passed or the current month
      while (monthDate <= currentMonth && monthDate <= fyEnd) {
        // Skip future months
        if (monthDate > currentMonth) {
          monthDate = addMonths(monthDate, 1);
          continue;
        }
        
        const monthName = format(monthDate, 'MMMM');
        const year = monthDate.getFullYear();
        
        // Filter cases for this month
        const monthCases = cases.filter(case_ => {
          const caseDate = parseISO(case_.created_at);
          return isSameMonth(caseDate, monthDate);
        });
        
        // Calculate totals for this month
        const monthTotals = monthCases.reduce((acc, case_) => ({
          invoiced: acc.invoiced + (Number(case_.invoiced_amount) || 0),
          reserve: acc.reserve + (Number(case_.reserve_amount) || 0),
          settlementAgreed: acc.settlementAgreed + (Number(case_.settlement_amount) || 0),
          paid: acc.paid + (Number(case_.paid_amount) || 0)
        }), {
          invoiced: 0,
          reserve: 0,
          settlementAgreed: 0,
          paid: 0
        });
        
        // Only add months that have data or are in the past
        if (!isSameMonth(monthDate, currentMonth)) {
          monthlyData.push({
            month: monthName,
            year,
            ...monthTotals
          });
        }
        
        monthDate = addMonths(monthDate, 1);
      }
      
      // Sort by date (newest first)
      monthlyData.sort((a, b) => {
        const dateA = new Date(`${a.month} ${a.year}`);
        const dateB = new Date(`${b.month} ${b.year}`);
        return dateB.getTime() - dateA.getTime();
      });
      
      setHistoricalMonthlyData(monthlyData);
    } catch (error) {
      console.error('Error fetching historical monthly data:', error);
    }
  };

  const fetchMonthlyTotals = async () => {
    try {
      const { fyStart, fyEnd } = getCurrentFinancialYear();
      
      const { data: cases, error } = await supabase
        .from('cases')
        .select('paid_amount, payment_date')
        .not('payment_date', 'is', null)
        .gte('payment_date', fyStart.toISOString())
        .lte('payment_date', fyEnd.toISOString());

      if (error) throw error;

      // Initialize all months in the FY with 0
      const months: Record<string, number> = {};
      let currentMonth = new Date(fyStart);
      while (currentMonth <= fyEnd) {
        months[format(currentMonth, 'MMM yyyy')] = 0;
        currentMonth = addMonths(currentMonth, 1);
      }

      // Sum up payments by month
      cases.forEach(case_ => {
        if (case_.payment_date && case_.paid_amount) {
          const paymentDate = parseISO(case_.payment_date);
          if (isWithinInterval(paymentDate, { start: fyStart, end: fyEnd })) {
            const monthKey = format(paymentDate, 'MMM yyyy');
            months[monthKey] = (months[monthKey] || 0) + Number(case_.paid_amount);
          }
        }
      });

      // Calculate yearly total from monthly data
      setYearlyTotal(Object.values(months).reduce((sum, val) => sum + (Number(val) || 0), 0));
    } catch (error) {
      console.error('Error fetching monthly totals:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6563ff]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-gray-900">
        Financial Summary
      </h1>
      
      {/* View Mode Toggle */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">Display Mode</h2>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button 
              onClick={() => setViewMode('currentMonth')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'currentMonth' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              Current Month
            </button>
            <button 
              onClick={() => setViewMode('financialYear')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'financialYear' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              Financial Year
            </button>
          </div>
        </div>
      </div>
      
      {/* Financial Summary Cards */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            {viewMode === 'currentMonth' 
              ? `Current Month: ${format(new Date(), 'MMMM yyyy')}` 
              : `Financial Year: ${format(getCurrentFinancialYear().fyStart, 'yyyy')}-${format(getCurrentFinancialYear().fyEnd, 'yy')}`}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-blue-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="h-8 w-8 text-blue-500" />
              <span className="text-2xl font-bold">
                ${(viewMode === 'currentMonth' ? financialSummary.invoiced : financialYearTotals.invoiced).toFixed(2)}
              </span>
            </div>
            <h3 className="text-lg font-semibold">Invoiced</h3>
            <p className="text-sm text-gray-600">Total amount invoiced</p>
          </div>

          <div className="bg-yellow-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="h-8 w-8 text-yellow-500" />
              <span className="text-2xl font-bold">
                ${(viewMode === 'currentMonth' ? financialSummary.reserve : financialYearTotals.reserve).toFixed(2)}
              </span>
            </div>
            <h3 className="text-lg font-semibold">Reserve</h3>
            <p className="text-sm text-gray-600">Total reserve amount</p>
          </div>

          <div className="bg-green-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="h-8 w-8 text-green-500" />
              <span className="text-2xl font-bold">
                ${(viewMode === 'currentMonth' ? financialSummary.settlementAgreed : financialYearTotals.settlementAgreed).toFixed(2)}
              </span>
            </div>
            <h3 className="text-lg font-semibold">Settlement Agreed</h3>
            <p className="text-sm text-gray-600">Total settlement amount</p>
          </div>

          <div className="bg-purple-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="h-8 w-8 text-purple-500" />
              <span className="text-2xl font-bold">
                ${(viewMode === 'currentMonth' ? financialSummary.paid : financialYearTotals.paid).toFixed(2)}
              </span>
            </div>
            <h3 className="text-lg font-semibold">Paid</h3>
            <p className="text-sm text-gray-600">Total amount collected</p>
          </div>
        </div>
      </div>
      
      {/* Previous Months Summary */}
      {historicalMonthlyData.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">Previous Months (FY {format(getCurrentFinancialYear().fyStart, 'yyyy')}-{format(getCurrentFinancialYear().fyEnd, 'yy')})</h2>
            <button className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
              <Download className="h-5 w-5" />
              <span>Export</span>
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoiced</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reserve</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Settlement Agreed</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {historicalMonthlyData.map((monthData, index) => (
                  <tr key={`${monthData.month}-${monthData.year}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{monthData.month} {monthData.year}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${monthData.invoiced.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${monthData.reserve.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${monthData.settlementAgreed.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${monthData.paid.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Financial Year Total</h2>
          <span className="text-3xl font-bold text-[#6563ff]">${yearlyTotal.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

export default FinancialSummaryPage;